import HeartIcon from 'components/icon/heart-icon';
import HollowHeartIcon from 'components/icon/hollow-heart-icon';
import ShareIcon from 'components/icon/share-icon';
import { PopRotateButton, SpinBurstButton } from 'components/atomic/atoms/animated-button';
import AnimatedTooltip from 'components/atomic/atoms/animated-tooltip';
import styles from './listing-action-buttons.module.scss';

export interface ListingActionButtonsProps {
  /** Whether the listing is liked/favorited */
  isLiked?: boolean;
  /** Callback when like button is clicked */
  onLikeClick?: () => void;
  /** Callback when share button is clicked */
  onShareClick?: () => void;
  /** Label for like button */
  likeLabel: string;
  /** Label for share button */
  shareLabel: string;
  /** Tooltip text when link is copied */
  copiedTooltip?: string;
  /** Whether to show the copied tooltip */
  showCopiedTooltip?: boolean;
}

/**
 * ListingActionButtons - Desktop action buttons for Like + Share
 * 
 * Pure presentational component - receives all labels and callbacks as props.
 * Uses atomic button variants and AnimatedTooltip components.
 */
export function ListingActionButtons({
  isLiked = false,
  onLikeClick,
  onShareClick,
  likeLabel,
  shareLabel,
  copiedTooltip,
  showCopiedTooltip = false,
}: ListingActionButtonsProps) {
  const buttonSx = {
    borderRadius: '100px',
    textTransform: 'none',
    boxShadow: 'none',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      boxShadow: 'none',
    },
  };

  return (
    <div className={styles.container} data-testid="ui-listing-action-buttons">
      <PopRotateButton
        icon={isLiked ? <HeartIcon /> : <HollowHeartIcon />}
        iconPosition="start"
        onClick={onLikeClick}
        className={`${styles.button} ${styles.likeButton}`}
        aria-label={likeLabel}
        sx={buttonSx}
      >
        {likeLabel}
      </PopRotateButton>
      
      <div className={styles.shareWrapper}>
        <SpinBurstButton
          icon={<ShareIcon />}
          iconPosition="start"
          onClick={onShareClick}
          className={`${styles.button} ${styles.shareButton}`}
          aria-label={shareLabel}
          sx={buttonSx}
        >
          {shareLabel}
        </SpinBurstButton>
        
        <AnimatedTooltip show={showCopiedTooltip} position="bottom">
          {copiedTooltip}
        </AnimatedTooltip>
      </div>
    </div>
  );
}

export default ListingActionButtons;
